<template>
  <div class="shift-notifications">
    <h3 class="notifications-title">
      <span class="material-icons">notifications</span>
      Alertes et notifications
    </h3>
    
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <span>Chargement des notifications...</span>
    </div>
    
    <div v-else-if="notifications.length === 0" class="empty-notifications">
      <span class="material-icons">notifications_none</span>
      <p>Aucune notification pour le moment</p>
    </div>
    
    <div v-else class="notifications-list">
      <ShiftNotification
        v-for="notification in notifications"
        :key="notification.id"
        :title="notification.title"
        :message="notification.message"
        :type="notification.type"
        :dismissible="true"
        @close="dismissNotification(notification.id)"
      />
    </div>
    
    <div class="notification-settings">
      <h4>Paramètres des notifications</h4>
      <div class="settings-group">
        <div class="setting-item">
          <label>
            <input type="checkbox" v-model="settings.notifyDelays" />
            Alertes pour retards fréquents
          </label>
        </div>
        <div class="setting-item">
          <label>
            <input type="checkbox" v-model="settings.notifyAnomalies" />
            Alertes pour anomalies de pointage
          </label>
        </div>
        <div class="setting-item">
          <label>
            <input type="checkbox" v-model="settings.dailyDigest" />
            Résumé quotidien
          </label>
        </div>
        <div class="setting-item threshold">
          <label>
            Seuil d'alerte pour retards (%)
            <input 
              type="number" 
              v-model.number="settings.delayThreshold" 
              min="1" 
              max="100" 
              step="1" 
            />
          </label>
        </div>
      </div>
      <button class="btn-save" @click="saveSettings">
        <span class="material-icons">save</span>
        Enregistrer les préférences
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import api from '@/services/api';
import ShiftNotification from './ShiftNotification.vue';

const loading = ref(true);
const notifications = ref([]);
const settings = ref({
  notifyDelays: true,
  notifyAnomalies: true,
  dailyDigest: false,
  delayThreshold: 10 // Pourcentage de retards à partir duquel une alerte est générée
});

// Charger les notifications
onMounted(async () => {
  await loadNotifications();
  await loadSettings();
  loading.value = false;
});

async function loadNotifications() {
  try {
    // Dans une implémentation réelle, cela viendrait d'une API
    // const response = await api.get('/notifications/shifts');
    // notifications.value = response.data;
    
    // Pour la démonstration, on utilise des données simulées
    setTimeout(() => {
      notifications.value = [
        {
          id: 1,
          title: 'Retards fréquents - Shift Matinée',
          message: 'Le taux de retard pour le shift Matinée a dépassé 15% cette semaine.',
          type: 'warning',
          date: new Date()
        },
        {
          id: 2,
          title: 'Anomalie détectée - Shift Après-midi',
          message: 'Plusieurs pointages manquants détectés pour le shift Après-midi du 18/07/2025.',
          type: 'error',
          date: new Date()
        },
        {
          id: 3,
          title: 'Amélioration - Shift Soirée',
          message: 'Le taux de ponctualité du shift Soirée s\'est amélioré de 8% cette semaine.',
          type: 'success',
          date: new Date()
        }
      ];
    }, 1000);
  } catch (error) {
    console.error('Erreur lors du chargement des notifications:', error);
    notifications.value = [];
  }
}

async function loadSettings() {
  try {
    // Dans une implémentation réelle, cela viendrait d'une API
    // const response = await api.get('/notifications/settings');
    // settings.value = response.data;
    
    // Pour la démonstration, on utilise des données simulées
    // Les valeurs par défaut sont déjà définies dans le ref
  } catch (error) {
    console.error('Erreur lors du chargement des paramètres de notification:', error);
  }
}

function dismissNotification(id) {
  // Dans une implémentation réelle, on enverrait une requête à l'API
  // await api.post(`/notifications/${id}/dismiss`);
  
  // Pour la démonstration, on supprime simplement de la liste locale
  notifications.value = notifications.value.filter(notification => notification.id !== id);
}

async function saveSettings() {
  try {
    // Dans une implémentation réelle, cela enverrait les paramètres à une API
    // await api.post('/notifications/settings', settings.value);
    
    // Pour la démonstration, on affiche simplement un message dans la console
    console.log('Paramètres de notification enregistrés:', settings.value);
    
    // Ajouter une notification de confirmation
    notifications.value.unshift({
      id: Date.now(),
      title: 'Paramètres enregistrés',
      message: 'Vos préférences de notification ont été mises à jour avec succès.',
      type: 'success',
      date: new Date()
    });
  } catch (error) {
    console.error('Erreur lors de l\'enregistrement des paramètres:', error);
  }
}
</script>

<style scoped>
/* Import des variables de couleurs OpSolver */
@import '@/assets/css/variables.css';

.shift-notifications {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 20px;
}

.notifications-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 0;
  margin-bottom: 20px;
  color: var(--turquoise-dark, #26A194);
  font-size: 1.2rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
  color: #666;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid var(--turquoise-primary, #30C7B5);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-notifications {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 0;
  color: #888;
}

.empty-notifications .material-icons {
  font-size: 48px;
  margin-bottom: 10px;
  color: #ccc;
}

.notifications-list {
  margin-bottom: 30px;
}

.notification-settings {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.notification-settings h4 {
  margin-top: 0;
  margin-bottom: 15px;
  color: var(--turquoise-dark, #26A194);
}

.settings-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 20px;
}

.setting-item {
  display: flex;
  align-items: center;
}

.setting-item.threshold {
  grid-column: span 2;
}

.setting-item label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.setting-item.threshold label {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
}

.setting-item input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: var(--turquoise-primary, #30C7B5);
}

.setting-item input[type="number"] {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: var(--turquoise-primary, #30C7B5);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-save:hover {
  background-color: var(--turquoise-dark, #26A194);
}
</style>
